// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { format } from "date-fns";
import { cn, formatNumber } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Package, Split, Save, Calculator, ArrowRight, ArrowLeft, ShoppingCart, Plus, PackageMinus, Lock, Printer, Tag, FileText, Sparkles, AlertTriangle, Check, X, ShieldAlert, Clock, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { EntityLink } from "@/components/shared/EntityLink";
import { ProcessGroupBadge } from "@/components/shared/ProcessGroupBadge";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { NumericInput } from "@/components/ui/numeric-input";
import { ComponentGroupRow } from "../components/ComponentGroupRow";
import { componentRankMap, isComponentTreeMode, orderComponentTree } from "../utils/component-tree";
import { SplitMaterialDialog } from "./SplitMaterialDialog";
import { ShopfloorMaterialStagingDialog } from "@/components/planning/ShopfloorMaterialStagingDialog";
import { MaterialStagingPrintTemplate, type MaterialStagingPrintData } from "@/components/planning/print/MaterialStagingPrintTemplate";
import { MaterialPickingListPrintModal, type PickingItem } from "@/components/planning/print/MaterialPickingListPrintModal";
import { calculatePaperPlan, calculateCutRatio, generateBackwardKHSX, F0PlanStep } from "@/utils/f0-allocation";

export function safeFormatDate(val: any, formatStr: string, fallback: string = ""): string {
  if (!val) return fallback;
  const d = new Date(val);
  return isNaN(d.getTime()) ? fallback : format(d, formatStr);
}

export function POMaterialTab({
  orderId,
  components = [],
  materialPlans = [],
  isPlanLocked = false,
}: {
  orderId: string;
  components: any[];
  materialPlans: any[];
  isPlanLocked?: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: poDetail } = useQuery({
    queryKey: ['production-order', orderId],
    queryFn: () => trpcClient.productionOrders.getById.query({ id: orderId }) as any,
    enabled: !!orderId,
  });

  const { data: rawStagedLogs, refetch: refetchStagedLogs } = useQuery({
    queryKey: ['material-planning', 'getStagedMaterialLogs', { productionOrderId: orderId }],
    queryFn: () => trpcClient.materialPlanning.getStagedMaterialLogs.query({ productionOrderId: orderId }) as any,
    enabled: !!orderId,
  });
  const stagedLogs = rawStagedLogs || [];
  // Lịch sử cấp phát: 1 dòng cho mỗi phiếu + vật tư (một lần cấp có thể trừ tồn từ nhiều dòng tồn kho)
  const issueRows = Object.values(
    stagedLogs.reduce((acc: Record<string, any>, l: any) => {
      const key = `${l.docNo || l.id}|${l.materialId || l.materialCode}`;
      if (!acc[key]) acc[key] = { ...l, quantity: 0 };
      acc[key].quantity += Number(l.quantity || 0);
      return acc;
    }, {}),
  ) as any[];
  // Lần cấp theo kế hoạch đã xuất thật (không tính cấp bù chờ duyệt/bị từ chối, dòng chờ tạo khi ban hành)
  const isRealIssueLog = (l: any) =>
    l.status === 'READY' && (l.approvalStatus ?? 'APPROVED') === 'APPROVED' && (l.issueType ?? 'PLANNED') === 'PLANNED';

  const { data: rawSupplyLogs, refetch: refetchSupplyLogs } = useQuery({
    queryKey: ['production-orders', 'getMaterialSupplyLogs', orderId],
    queryFn: () => trpcClient.productionOrders.getMaterialSupplyLogs.query({ poId: orderId }) as any,
    enabled: !!orderId,
  });
  const supplyLogs: any[] = rawSupplyLogs || [];

  const { data: rawPoInventoryDocs, refetch: refetchPoInventoryDocs, isLoading: isLoadingInventory } = useQuery({
    queryKey: ['inventory', 'getDocumentsByProductionOrder', { productionOrderId: orderId }],
    queryFn: () => trpcClient.inventory.getDocumentsByProductionOrder.query({ productionOrderId: orderId }) as any,
    enabled: !!orderId,
  });
  const inventoryDocs = rawPoInventoryDocs || [];
  const [showPickingModal, setShowPickingModal] = useState(false);
  
  // Flatten lines for display
  const actualMaterialLines = inventoryDocs.flatMap((doc: any) => 
    doc.lines.map((line: any) => ({
      ...line,
      docType: doc.docType,
      docNo: doc.docNo,
      docDate: doc.docDate,
      docId: doc.id,
      status: doc.status
    }))
  ).filter((l: any) => l.status === 'COMPLETED');

  const [isCalculating, setIsCalculating] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  // Cấp bù phế phẩm / Hư hỏng (Wave 2)
  const [isCompensationOpen, setIsCompensationOpen] = useState(false);
  const [compensationForm, setCompensationForm] = useState<{
    materialId: string;
    quantity: number;
    defectProcessId: string;
    defectReason: string;
    note: string;
  }>({
    materialId: '',
    quantity: 0,
    defectProcessId: '',
    defectReason: 'Rách phôi / kẹt giấy máy in',
    note: '',
  });
  const [isSubmittingCompensation, setIsSubmittingCompensation] = useState(false);

  // Phê duyệt cấp bù (Trưởng ca / Quản đốc)
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [selectedCompensationLog, setSelectedCompensationLog] = useState<any>(null);
  const [approvalDecision, setApprovalDecision] = useState<boolean>(true);
  const [approvalNotes, setApprovalNotes] = useState<string>('');
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);

  const handleRequestCompensation = async () => {
    if (!compensationForm.materialId) {
      toast.error("Vui lòng chọn vật tư cần cấp bù");
      return;
    }
    if (!compensationForm.quantity || compensationForm.quantity <= 0) {
      toast.error("Vui lòng nhập số lượng cấp bù > 0");
      return;
    }
    if (!compensationForm.defectReason?.trim()) {
      toast.error("Vui lòng nhập lý do phế phẩm / hư hỏng");
      return;
    }

    try {
      setIsSubmittingCompensation(true);
      await trpcClient.productionOrders.requestScrapCompensation.mutate({
        productionOrderId: orderId,
        materialId: compensationForm.materialId,
        quantity: Number(compensationForm.quantity),
        defectProcessId: compensationForm.defectProcessId || undefined,
        defectReason: compensationForm.defectReason.trim(),
        note: compensationForm.note?.trim() || undefined,
      });
      toast.success("Đã gửi yêu cầu cấp bù phế phẩm thành công! Chờ Quản đốc duyệt.");
      setIsCompensationOpen(false);
      setCompensationForm({
        materialId: '',
        quantity: 0,
        defectProcessId: '',
        defectReason: 'Rách phôi / kẹt giấy máy in',
        note: '',
      });
      refetchSupplyLogs();
      queryClient.invalidateQueries({ queryKey: ['production-order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['material-planning'] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi gửi yêu cầu cấp bù");
    } finally {
      setIsSubmittingCompensation(false);
    }
  };

  const handleApproveCompensation = async () => {
    if (!selectedCompensationLog) return;
    try {
      setIsSubmittingApproval(true);
      await trpcClient.productionOrders.approveScrapCompensation.mutate({
        logId: selectedCompensationLog.id,
        approved: approvalDecision,
        approvalNotes: approvalNotes?.trim() || undefined,
      });
      toast.success(approvalDecision ? "Đã duyệt cấp bù phế phẩm và điều phối kho!" : "Đã từ chối yêu cầu cấp bù!");
      setIsApprovalDialogOpen(false);
      setSelectedCompensationLog(null);
      setApprovalNotes('');
      refetchSupplyLogs();
      queryClient.invalidateQueries({ queryKey: ['production-order', orderId] });
      queryClient.invalidateQueries({ queryKey: ['material-planning'] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi xử lý phê duyệt");
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const [splitPlan, setSplitPlan] = useState<any | null>(null);
  const [stagingPlan, setStagingPlan] = useState<any | null>(null);
  const [reprintData, setReprintData] = useState<{
    data: MaterialStagingPrintData;
    mode: 'DOCUMENT' | 'PALLET_DECAL';
  } | null>(null);
  const reprintRef = useRef<HTMLDivElement>(null);

  const handleReprint = (log: any, mode: 'DOCUMENT' | 'PALLET_DECAL') => {
    setReprintData({
      mode,
      data: {
        docNo: log.docNo || 'XUAT-SX',
        docDate: log.createdAt || new Date(),
        poCode: poDetail?.orderId || poDetail?.poReference || orderId,
        productName: poDetail?.productName || poDetail?.product?.productName || '—',
        customerName: poDetail?.customer?.name || poDetail?.customerName || '',
        materialCode: log.materialCode,
        materialName: log.materialName,
        unit: log.unit || 'tờ',
        targetStepName: log.routingStepName || 'In Offset',
        warehouseName: log.warehouseName || 'Kho Vật Tư',
        notes: log.note || '',
        lots: [
          {
            lotNumber: log.lotNumber,
            quantity: Number(log.quantity),
            locationCode: '',
          },
        ],
        totalQuantity: Number(log.quantity),
      },
    });
  };

  const handleTriggerReprint = () => {
    const printEl = reprintRef.current;
    if (!printEl) return;
    const printWindow = window.open('', '_blank', 'width=1100,height=850');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${reprintData?.mode === 'PALLET_DECAL' ? 'THE_PALLET' : 'PHIEU_CAP_PHAT'}_${poDetail?.orderId || orderId}</title>
          <style>
            @page {
              size: ${reprintData?.mode === 'PALLET_DECAL' ? 'A5 landscape' : 'A4 portrait'};
              margin: 10mm;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              margin: 0;
              padding: 0;
              color: #000;
              background: #fff;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          </style>
          <script src="https://cdn.tailwindcss.com"></script>
        </head>
        <body>
          ${printEl.innerHTML}
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isLamination, setIsLamination] = useState(false);
  const [lamination2Sides, setLamination2Sides] = useState(false);
  const [paperShortEdge, setPaperShortEdge] = useState(43);
  const [paperQty, setPaperQty] = useState(1000);
  const [addForm, setAddForm] = useState<any>({});
  
  useEffect(() => {
    if (isLamination) {
      const meters = (paperShortEdge / 100) * paperQty * (lamination2Sides ? 2 : 1);
      setAddForm((prev: any) => ({ ...prev, qtyRequired: Math.ceil(meters), unit: 'm' }));
    }
  }, [isLamination, lamination2Sides, paperShortEdge, paperQty]);

  const [isDecomposing, setIsDecomposing] = useState(false);

  const handleAutoDecompose = async () => {
    setIsDecomposing(true);
    try {
      const res = (await trpcClient.productionOrders.autoDecomposeAuxiliary.mutate({
        id: orderId,
      })) as any;
      if (res?.addedCount > 0) {
        toast.success(`Đã tự động bóc tách và bổ sung ${res.addedCount} vật tư phụ theo công đoạn!`);
      } else {
        toast.warning("Các công đoạn đã có đủ định mức vật tư phụ, không cần bổ sung.");
      }
      await queryClient.invalidateQueries({ queryKey: ["production-order"] });
      await queryClient.invalidateQueries({ queryKey: ["material-planning"] });
      refetchStagedLogs();
    } catch (err: any) {
      toast.error(err?.message || "Bóc tách vật tư phụ thất bại");
    } finally {
      setIsDecomposing(false);
    }
  };

  const handleAddMaterial = async () => {
    if (!addForm.materialId) {
      toast.error("Vui lòng chọn vật tư");
      return;
    }
    if (!addForm.qtyRequired || addForm.qtyRequired <= 0) {
      toast.error("Vui lòng nhập số lượng > 0");
      return;
    }
    
    try {
      await trpcClient.productionOrders.addBomItem.mutate({
        id: orderId,
        data: {
          componentId: addForm.componentId || (components.length > 0 ? components[0].id : null),
          materialId: addForm.materialId,
          qtyRequired: Number(addForm.qtyRequired),
          wastagePercent: Number(addForm.wastagePercent || 0),
          unit: addForm.unit || "Tờ",
          routingStepId: addForm.routingStepId || null,
          specification: addForm.specification || null,
        }
      });
      toast.success("Thêm vật tư thành công");
      setIsAddOpen(false);
      setAddForm({});
      await queryClient.invalidateQueries({ queryKey: ["production-order"] });
      await queryClient.invalidateQueries({ queryKey: ["material-planning"] });
      refetchStagedLogs();
    } catch (e: any) {
      toast.error("Lỗi: " + e.message);
    }
  };

  // Auto-fill calc routing from components if available
  const defaultRouting = components[0]?.routingSteps
    ? components[0].routingSteps.map((s: any) => s.name).join(", ")
    : "In Offset, Cán màng, Bế, Dán hộp";

  // States for Live Paper Calculator
  const [calcQtyOrder, setCalcQtyOrder] = useState<number>(10000);
  const [calcPcsPerSheet, setCalcPcsPerSheet] = useState<number>(4);
  const [calcRouting, setCalcRouting] = useState<string>(defaultRouting);
  
  const [calcPurchasedSize, setCalcPurchasedSize] = useState<string>("65 x 86 cm");
  const [calcPrintingSize, setCalcPrintingSize] = useState<string>("43 x 65 cm");
  const [calcCustomRatio, setCalcCustomRatio] = useState<number>(2);

  // Update routing if components prop changes
  useEffect(() => {
    if (components[0]?.routingSteps) {
      setCalcRouting(components[0].routingSteps.map((s: any) => s.name).join(", "));
    }
  }, [components]);

  // Backward Allocation Engine execution
  const backwardPlan = generateBackwardKHSX(calcQtyOrder, calcPcsPerSheet, calcRouting);
  
  // Find "In" step to get the gross sheets for paper calculation
  const printStep = backwardPlan.find(s => s.cong_doan.toLowerCase().includes("in"));
  const totalPlanSheets = printStep ? (printStep.sl_cap || printStep.sl_yeu_cau) : Math.ceil(calcQtyOrder / calcPcsPerSheet);

  const paperResult = calculatePaperPlan({
    qtyOrder: calcQtyOrder,
    pcsPerSheet: calcPcsPerSheet,
    purchasedPaperSize: calcPurchasedSize,
    printingPaperSize: calcPrintingSize,
    customCutRatio: calcCustomRatio,
    totalPlanSheets: Number(totalPlanSheets),
  });

  // Cây Sản phẩm → Hợp phần: nhiều hợp phần thì sắp dòng vật tư theo hợp phần và chèn dòng tiêu đề nhóm
  const treeMode = isComponentTreeMode(components);
  const componentNodes = orderComponentTree(components);
  const componentRank = componentRankMap(componentNodes);
  const nodeById = new Map(componentNodes.map((n) => [n.id, n]));
  const byComponent = (a: any, b: any) => componentRank(a.componentId) - componentRank(b.componentId);
  const printMaterials = materialPlans.filter((p) => p.isPrint).sort(treeMode ? byComponent : () => 0);
  const accessories = materialPlans.filter((p) => !p.isPrint).sort(treeMode ? byComponent : () => 0);

  // Đã cấp theo kế hoạch (chỉ lần cấp thật) / còn thiếu của từng dòng — dùng cho tiến độ, thao tác dòng và bulk
  const issuedQtyOf = (plan: any) => {
    const matId = plan.plannedMaterial?.id || plan.plannedMaterialId || plan.materialId;
    const matCode = plan.plannedMaterial?.code || plan.plannedMaterial?.materialId || plan.materialCode;
    return stagedLogs
      .filter((l: any) => (matId && l.materialId === matId) || (matCode && l.materialCode === matCode))
      .filter(isRealIssueLog)
      .reduce((s: number, l: any) => s + Number(l.quantity || 0), 0);
  };
  const remainingQtyOf = (plan: any) => Math.max(0, Number(plan.qtyRequired || plan.plannedQuantity || 0) - issuedQtyOf(plan));

  // Chọn nhanh dòng trong bảng inline (mỗi bảng một bộ chọn) → thanh thao tác hàng loạt theo trạng thái dòng
  const [printSel, setPrintSel] = useState<Set<string>>(new Set());
  const [accSel, setAccSel] = useState<Set<string>>(new Set());
  const [pickingFilter, setPickingFilter] = useState<Set<string> | null>(null);
  const toggleIn = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };
  const toggleAllIn = (set: Set<string>, setter: (s: Set<string>) => void, list: any[]) =>
    setter(set.size === list.length ? new Set() : new Set(list.map((p: any) => p.id)));

  const openCompensationFor = (plan?: any) => {
    const mat = plan || materialPlans[0];
    setCompensationForm({
      materialId: mat?.plannedMaterialId || mat?.materialId || "",
      quantity: 0,
      defectProcessId: "",
      defectReason: "Rách phôi / kẹt giấy máy in",
      note: "",
    });
    setIsCompensationOpen(true);
  };

  const issueSelected = (ids: string[]) =>
    navigate({
      to: '/warehouse/material/transactions/new' as any,
      search: { docType: 'ISSUE', type: 'MATERIAL', productionOrderId: orderId, referenceDoc: poDetail?.orderId || orderId, planIds: ids.join(',') } as any,
    });

  /** Thao tác hàng loạt theo trạng thái các dòng đã chọn: còn thiếu → Xuất kho (một phiếu cho các dòng);
   *  đã cấp đủ → Cấp bù (phiếu cấp bù theo từng vật tư nên chỉ khi chọn đúng 1 dòng); luôn có In bảng kê */
  const renderBulk = (list: any[], sel: Set<string>, clear: () => void, testId: string) => {
    const chosen = list.filter((p: any) => sel.has(p.id));
    const short = chosen.filter((p: any) => remainingQtyOf(p) > 0);
    const done = chosen.filter((p: any) => remainingQtyOf(p) <= 0);
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
        <span className="text-xs font-semibold text-indigo-700 mr-1">Đã chọn {chosen.length}</span>
        {short.length > 0 && (
          <Button size="sm" className="h-8 text-xs gap-1.5" data-testid="po-material-bulk-issue" onClick={() => issueSelected(short.map((p: any) => p.id))}>
            <Package className="h-3.5 w-3.5" /> Xuất kho ({short.length})
          </Button>
        )}
        {done.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
            data-testid="po-material-bulk-scrap"
            disabled={done.length !== 1}
            title={done.length !== 1 ? "Cấp bù lập cho từng vật tư: chọn 1 dòng đã cấp đủ" : "Lập yêu cầu cấp bù cho vật tư đã chọn"}
            onClick={() => openCompensationFor(done[0])}
          >
            <ShieldAlert className="h-3.5 w-3.5" /> Cấp bù
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" data-testid="po-material-bulk-print" onClick={() => { setPickingFilter(new Set(chosen.map((p: any) => p.id))); setShowPickingModal(true); }}>
          <FileText className="h-3.5 w-3.5" /> In bảng kê
        </Button>
        <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clear}>Bỏ chọn</Button>
      </div>
    );
  };
  const groupHeader = (list: any[], idx: number, colSpan: number) => {
    if (!treeMode) return null;
    const compId = list[idx]?.componentId || null;
    if (idx > 0 && (list[idx - 1]?.componentId || null) === compId) return null;
    return (
      <ComponentGroupRow
        node={compId ? nodeById.get(compId) || null : null}
        colSpan={colSpan}
        count={list.filter((x) => (x.componentId || null) === compId).length}
        unitLabel="vật tư"
      />
    );
  };

  const pickingItems: PickingItem[] = [
    ...printMaterials.map((p: any) => {
      const reqQty = Number(p.qtyRequired || p.plannedQuantity || 0);
      const issQty = Number(p.qtyIssued || p.qtyAvailable || 0);
      return {
        id: p.id,
        category: 'Vật tư in',
        materialCode: p.plannedMaterial?.code || p.plannedMaterial?.materialId || p.materialCode || 'VT',
        materialName: p.plannedMaterial?.name || p.materialName || 'Giấy in',
        unit: p.unit || 'Tờ',
        plannedQty: reqQty,
        issuedQty: issQty,
        pendingQty: Math.max(0, reqQty - issQty),
        locationCode: 'Kệ Giấy (KHO-VT)',
        notes: p.component?.componentName ? `Bộ phận: ${p.component.componentName}` : '',
      };
    }),
    ...accessories.map((p: any) => {
      const reqQty = Number(p.qtyRequired || p.plannedQuantity || 0);
      const issQty = Number(p.qtyIssued || p.qtyAvailable || 0);
      return {
        id: p.id,
        category: 'Vật tư phụ',
        materialCode: p.plannedMaterial?.code || p.plannedMaterial?.materialId || p.materialCode || 'VT-PHU',
        materialName: p.plannedMaterial?.name || p.materialName || 'Vật tư phụ',
        unit: p.unit || 'Đơn vị',
        plannedQty: reqQty,
        issuedQty: issQty,
        pendingQty: Math.max(0, reqQty - issQty),
        locationCode: 'Kệ Phụ Liệu (KHO-VT)',
        notes: p.routingStep?.name ? `Công đoạn: ${p.routingStep.name}` : '',
      };
    }),
  ];

  const handleInstantPatchBOMItem = async (bomId: string, patchData: Record<string, any>) => {
    if (!bomId) return;
    try {
      await trpcClient.productionOrders.updateBOMItemInline.mutate({
        bomId,
        data: patchData,
      });
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      toast.success("Đã cập nhật vật tư BOM");
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi cập nhật vật tư BOM");
    }
  };


  const startEdit = (plan: any) => {
    setEditingPlan(plan.id);
    setEditForm({
      setupSheets: plan.setupSheets,
      wastePercentage: plan.wastePercentage,
    });
  };

  const saveEdit = async (planId: string) => {
    try {
      await trpcClient.materialPlanning.update.mutate({
        id: planId,
        data: {
          setupSheets: editForm.setupSheets,
          wastePercentage: editForm.wastePercentage,
        },
      });
      toast.success("Cập nhật thành công");
      setEditingPlan(null);
      queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi cập nhật");
    }
  };

  // Phase 225: Link-To-Form Material Issuance to Canonical TransactionForm
  const handleNavigateToIssueForm = (specificItem?: any) => {
    if (specificItem) {
      const matId = specificItem.plannedMaterial?.id || specificItem.plannedMaterialId || specificItem.materialId;
      const matCode = specificItem.plannedMaterial?.code || specificItem.plannedMaterial?.materialId || specificItem.materialCode || '';
      const matName = specificItem.plannedMaterial?.name || specificItem.materialName || 'Vật tư';
      const pendingQty = Math.max(0, Number(specificItem.qtyRequired || 0) - Number(specificItem.qtyAvailable || specificItem.qtyIssued || 0));

      navigate({
        to: '/warehouse/material/transactions/new' as any,
        search: {
          docType: 'ISSUE',
          type: 'MATERIAL',
          productionOrderId: orderId,
          referenceDoc: poDetail?.orderId || orderId,
          itemId: matId,
          itemCode: matCode,
          itemName: matName,
          quantity: pendingQty > 0 ? pendingQty : Number(specificItem.qtyRequired || 1),
          unit: specificItem.unit || 'Tờ',
          lineNotes: `Xuất cho LSX ${poDetail?.orderId || orderId}`,
        } as any,
      });
      return;
    }

    navigate({
      to: '/warehouse/material/transactions/new' as any,
      search: {
        docType: 'ISSUE',
        type: 'MATERIAL',
        productionOrderId: orderId,
        referenceDoc: poDetail?.orderId || orderId,
      } as any,
    });
  };


  const totalPlannedQty = materialPlans.reduce((sum: number, p: any) => sum + Number(p.qtyRequired || p.plannedQuantity || 0), 0);
  const scrapLogs = supplyLogs.filter((l: any) => l.issueType === 'SCRAP_COMPENSATION');
  const approvedScrapQty = scrapLogs
    .filter((l: any) => l.approvalStatus === 'APPROVED')
    .reduce((sum: number, l: any) => sum + Number(l.quantity || 0), 0);
  const pendingScrapCount = scrapLogs.filter((l: any) => l.approvalStatus === 'PENDING_APPROVAL').length;
  const scrapRatio = totalPlannedQty > 0 ? ((approvedScrapQty / totalPlannedQty) * 100).toFixed(1) : "0.0";

  return (
    <div className="space-y-6">
      <Card className="shadow-sm border-indigo-100">
        {/* Đầu bảng: thao tác cho cả lệnh; chọn dòng → thao tác hàng loạt cho các dòng đã chọn */}
        <CardHeader className="bg-slate-50/50 py-2.5 border-b border-indigo-50 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0" data-testid="po-material-header">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <CardTitle className="text-sm font-semibold text-slate-800">Vật tư in</CardTitle>
            <Badge
              className={cn(
                "font-semibold text-[10px] border-none shadow-none",
                poDetail?.materialStatus === "READY"
                  ? "bg-emerald-100 text-emerald-800"
                  : poDetail?.materialStatus === "PARTIAL"
                    ? "bg-amber-100 text-amber-800"
                    : "bg-slate-200 text-slate-700",
              )}
            >
              {poDetail?.materialStatus === "READY" ? "Sẵn sàng" : poDetail?.materialStatus === "PARTIAL" ? "Cấp một phần" : "Chờ cấp"}
            </Badge>
            {pendingScrapCount > 0 && (
              <Badge className="bg-orange-100 text-orange-800 border-none text-[10px]">{pendingScrapCount} cấp bù chờ duyệt</Badge>
            )}
          </div>
          {printSel.size > 0 ? (
            renderBulk(printMaterials, printSel, () => setPrintSel(new Set()), "po-material-print-bulk")
          ) : (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="po-material-order-actions">
              <Button size="sm" className="h-8 text-xs gap-1.5" onClick={() => handleNavigateToIssueForm()} title="Lập phiếu xuất kho vật tư cho cả lệnh">
                <Package className="h-3.5 w-3.5" /> Xuất kho
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => { setPickingFilter(null); setShowPickingModal(true); }} title="In bảng kê nhặt hàng của cả lệnh">
                <FileText className="h-3.5 w-3.5" /> In bảng kê
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50" onClick={() => openCompensationFor()}>
                <ShieldAlert className="h-3.5 w-3.5" /> Cấp bù
              </Button>
              <button
                type="button"
                className="text-xs text-indigo-600 hover:underline px-1"
                onClick={() => navigate({ to: "/material-planning", search: { poId: poDetail?.orderId || orderId, tab: "po" } as any })}
              >
                Mở KHVT →
              </button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-10 text-center">
                  <Checkbox
                    aria-label="Chọn tất cả vật tư in"
                    checked={printMaterials.length > 0 && printSel.size === printMaterials.length}
                    onCheckedChange={() => toggleAllIn(printSel, setPrintSel, printMaterials)}
                  />
                </TableHead>
                <TableHead>Mã / Tên Vật tư</TableHead>
                <TableHead>Thành phần</TableHead>
                <TableHead>Bài in</TableHead>
                <TableHead className="text-right">SL cần</TableHead>
                <TableHead className="text-right">Bù hao chạy</TableHead>
                <TableHead className="text-right">Bù hao thiết lập</TableHead>
                <TableHead className="text-right">Tổng cần</TableHead>
                <TableHead className="w-36 text-center">Hạn cần VT</TableHead>
                <TableHead className="w-40 text-center">Tiến Độ Cấp Phát</TableHead>
                <TableHead className="text-right w-32 sticky right-0 z-10 bg-slate-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {printMaterials.length > 0 ? (
                printMaterials.map((plan: any, idx: number) => (
                  <React.Fragment key={plan.id}>
                  {groupHeader(printMaterials, idx, 11)}
                  <TableRow data-testid="po-material-print-row" className={printSel.has(plan.id) ? "bg-indigo-50/40" : undefined}>
                    <TableCell className="text-center">
                      <Checkbox aria-label="Chọn dòng" checked={printSel.has(plan.id)} onCheckedChange={() => toggleIn(printSel, setPrintSel, plan.id)} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const matId = plan.plannedMaterial?.id || plan.plannedMaterialId || plan.materialId;
                        const matCode = plan.plannedMaterial?.code || plan.plannedMaterial?.materialId || plan.materialCode;
                        const matName = plan.plannedMaterial?.name || plan.materialName;
                        return (
                          <div>
                            <div className="font-medium">
                              {matId ? (
                                <EntityLink
                                  entityType="material"
                                  id={matId}
                                  code={matCode}
                                  search={{ returnUrl: `/production-orders/${orderId}?tab=materials` }}
                                />
                              ) : (
                                <span>{matCode}</span>
                              )}
                              {plan.qtyIssued > 0 && <span title="Đã xuất kho/khóa"><Lock className="inline-block h-3 w-3 text-red-500 ml-1" /></span>}
                            </div>
                            <div className="text-xs text-slate-500">
                              {matName}
                            </div>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {plan.component?.componentName || "-"}
                    </TableCell>
                    <TableCell>
                      {plan.printLayout ? (
                        <Badge
                          variant="outline"
                          className="bg-blue-50 text-blue-700"
                        >
                          {plan.printLayout.name}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 italic text-xs">
                          Không có
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {plan.plannedQuantity} {plan.unit}
                    </TableCell>

                    {/* Inline Edit for Waste % */}
                    <TableCell className="text-right">
                      {editingPlan === plan.id ? (
                        <NumericInput
                          value={editForm.wastePercentage}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              wastePercentage: Number(e.target.value),
                            })
                          }
                          className="w-20 text-right ml-auto h-8"
                        />
                      ) : (
                        <span className="font-mono">
                          {plan.wastePercentage}%
                        </span>
                      )}
                    </TableCell>

                    {/* Inline Edit for Setup Sheets */}
                    <TableCell className="text-right">
                      {editingPlan === plan.id ? (
                        <NumericInput
                          value={editForm.setupSheets}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              setupSheets: Number(e.target.value),
                            })
                          }
                          className="w-20 text-right ml-auto h-8"
                        />
                      ) : (
                        <span className="font-mono">{plan.setupSheets}</span>
                      )}
                    </TableCell>

                    <TableCell className="text-right font-mono font-semibold text-indigo-700">
                      {plan.qtyRequired} {plan.unit}
                    </TableCell>

                    <TableCell className="text-center font-mono">
                      {plan.expectedDate && !isNaN(new Date(plan.expectedDate).getTime()) ? (
                        <div className="flex flex-col items-center">
                          <span className="font-semibold text-xs text-slate-800">
                            {safeFormatDate(plan.expectedDate, "dd/MM/yyyy")}
                          </span>
                          <span className="text-[10px] text-indigo-600 font-medium">
                            {safeFormatDate(plan.expectedDate, "HH:mm")} (Sẵn sàng)
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic text-xs">Trước giờ in</span>
                      )}
                    </TableCell>

                    {/* Live Staging Progress */}
                    <TableCell>
                      {(() => {
                        const matId = plan.plannedMaterial?.id || plan.plannedMaterialId || plan.materialId;
                        const matCode = plan.plannedMaterial?.code || plan.plannedMaterial?.materialId || plan.materialCode;
                        // Chỉ tính lần cấp theo kế hoạch đã xuất thật; hàng mới mua về cho lệnh hiện "Đã về kho, chờ cấp" (FB-CDA00F)
                        const qtyIss = stagedLogs
                          .filter((l: any) => (matId && l.materialId === matId) || (matCode && l.materialCode === matCode))
                          .filter(isRealIssueLog)
                          .reduce((s: number, l: any) => s + Number(l.quantity || 0), 0);
                        const qtyReq = Number(plan.qtyRequired || plan.plannedQuantity || 1);
                        const percent = Math.min(100, Math.round((qtyIss / qtyReq) * 100));
                        const isFulfilled = qtyIss >= qtyReq;
                        const isReceivedWaiting = !isFulfilled && qtyIss + Number(plan.qtyReceivedForPlan || 0) >= qtyReq;

                        return (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-[11px] font-mono">
                              <span className="font-bold text-slate-700">
                                {qtyIss.toLocaleString('vi-VN')} / {qtyReq.toLocaleString('vi-VN')} {plan.unit}
                              </span>
                              <span className={`font-bold ${isFulfilled ? 'text-emerald-600' : qtyIss > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                                {percent}%
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  isFulfilled ? 'bg-emerald-500' : qtyIss > 0 ? 'bg-blue-500' : 'bg-slate-300'
                                }`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <div className="text-center">
                              {isFulfilled ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9px] py-0 h-4">
                                  ✓ ĐÃ CẤP ĐỦ
                                </Badge>
                              ) : isReceivedWaiting ? (
                                <Badge className="bg-sky-100 text-sky-800 border-sky-300 text-[9px] py-0 h-4">
                                  ĐÃ VỀ KHO, CHỜ CẤP
                                </Badge>
                              ) : qtyIss > 0 ? (
                                <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[9px] py-0 h-4">
                                  ĐANG CẤP {percent}%
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-500 text-[9px] py-0 h-4">
                                  CHƯA CẤP
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </TableCell>

                    <TableCell className="text-right sticky right-0 z-10 bg-white shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]">
                      {editingPlan === plan.id ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingPlan(null)}
                          >
                            Hủy
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => saveEdit(plan.id)}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 font-semibold px-2 text-xs"
                            onClick={() => setStagingPlan(plan)}
                            disabled={remainingQtyOf(plan) <= 0}
                            data-testid="po-material-row-issue"
                            title="Cấp vật tư từ kho ra sàn máy"
                          >
                            <Package className="h-3.5 w-3.5 mr-1 text-amber-700" />
                            Cấp vật tư
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-indigo-600"
                            onClick={() => startEdit(plan)}
                            disabled={isPlanLocked || plan.qtyIssued > 0}
                            title="Sửa bù hao"
                            aria-label="Sửa bù hao"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-amber-600 hover:bg-amber-50"
                            onClick={() => setSplitPlan(plan)}
                            disabled={isPlanLocked || plan.qtyIssued > 0}
                            title="Tách vật tư"
                            aria-label="Tách vật tư"
                          >
                            <Split className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="text-center py-6 text-slate-500"
                  >
                    Không có vật tư in. Hãy tính MRP để sinh dữ liệu.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-slate-50/50 py-2.5 border-b border-slate-100 flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
          <CardTitle className="text-sm font-semibold text-slate-800">Vật tư phụ</CardTitle>
          {accSel.size > 0 ? (
            renderBulk(accessories, accSel, () => setAccSel(new Set()), "po-material-acc-bulk")
          ) : !isPlanLocked && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleAutoDecompose}
                disabled={isDecomposing}
                className="h-8 text-xs font-medium text-indigo-700 border-indigo-200 hover:bg-indigo-50 flex items-center gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
                {isDecomposing ? "Đang bóc tách..." : "Bóc tách"}
              </Button>
              <Button
                size="sm"
                onClick={() => setIsAddOpen(true)}
                className="h-8 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1 shadow-xs"
              >
                <Plus className="h-3.5 w-3.5" />
                Thêm vật tư
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-10 text-center">
                  <Checkbox
                    aria-label="Chọn tất cả vật tư phụ"
                    checked={accessories.length > 0 && accSel.size === accessories.length}
                    onCheckedChange={() => toggleAllIn(accSel, setAccSel, accessories)}
                  />
                </TableHead>
                <TableHead>Mã / Tên Vật tư</TableHead>
                <TableHead>Công đoạn</TableHead>
                <TableHead>Thành phần</TableHead>
                <TableHead className="text-right">SL cần</TableHead>
                <TableHead className="text-right">Bù hao chạy</TableHead>
                <TableHead className="text-right">Tổng cần</TableHead>
                <TableHead className="w-36 text-center">Hạn cần VT</TableHead>
                <TableHead className="w-40 text-center">Tiến Độ Cấp Phát</TableHead>
                <TableHead className="text-right w-28 sticky right-0 z-10 bg-slate-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accessories.length > 0 ? (
                accessories.map((plan: any, idx: number) => {
                  const rStep = plan.routingStep || (poDetail?.routingSteps || []).find((s: any) => s.id === plan.routingStepId);
                  return (
                  <React.Fragment key={plan.id}>
                  {groupHeader(accessories, idx, 10)}
                  <TableRow data-testid="po-material-acc-row" className={accSel.has(plan.id) ? "bg-indigo-50/40" : undefined}>
                    <TableCell className="text-center">
                      <Checkbox aria-label="Chọn dòng" checked={accSel.has(plan.id)} onCheckedChange={() => toggleIn(accSel, setAccSel, plan.id)} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const matId = plan.plannedMaterial?.id || plan.plannedMaterialId || plan.materialId;
                        const matCode = plan.plannedMaterial?.code || plan.plannedMaterial?.materialId || plan.materialCode;
                        const matName = plan.plannedMaterial?.name || plan.materialName;
                        return (
                          <div>
                            <div className="font-medium">
                              {matId ? (
                                <EntityLink
                                  entityType="material"
                                  id={matId}
                                  code={matCode}
                                  search={{ returnUrl: `/production-orders/${orderId}?tab=materials` }}
                                />
                              ) : (
                                <span>{matCode}</span>
                              )}
                              {plan.qtyIssued > 0 && <span title="Đã xuất kho/khóa"><Lock className="inline-block h-3 w-3 text-red-500 ml-1" /></span>}
                            </div>
                            <div className="text-xs text-slate-500">
                              {matName}
                            </div>
                          </div>
                        );
                      })()}
                      {plan.suppliedSize && (
                        <div className="text-[10px] text-slate-400 italic">
                          {plan.suppliedSize}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {rStep ? (
                        <div className="space-y-1">
                          <div className="text-xs font-semibold text-slate-700">
                            {rStep.name}
                          </div>
                          <ProcessGroupBadge
                            group={rStep.process?.group}
                            name={rStep.name}
                            showDetail={false}
                            size="sm"
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {plan.component?.componentName || "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {plan.plannedQuantity} {plan.unit}
                    </TableCell>

                    <TableCell className="text-right">
                      {editingPlan === plan.id ? (
                        <NumericInput
                          value={editForm.wastePercentage}
                          onChange={(e) =>
                            setEditForm({
                              ...editForm,
                              wastePercentage: Number(e.target.value),
                            })
                          }
                          className="w-20 text-right ml-auto h-8"
                        />
                      ) : (
                        <span className="font-mono">
                          {plan.wastePercentage}%
                        </span>
                      )}
                    </TableCell>

                    <TableCell className="text-right font-mono font-semibold text-slate-700">
                      {plan.qtyRequired} {plan.unit}
                    </TableCell>

                    <TableCell className="text-center font-mono">
                      {plan.expectedDate && !isNaN(new Date(plan.expectedDate).getTime()) ? (
                        <div className="flex flex-col items-center">
                          <span className="font-semibold text-xs text-slate-800">
                            {safeFormatDate(plan.expectedDate, "dd/MM/yyyy")}
                          </span>
                          <span className="text-[10px] text-indigo-600 font-medium">
                            {safeFormatDate(plan.expectedDate, "HH:mm")}
                          </span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic text-xs">Theo công đoạn</span>
                      )}
                    </TableCell>

                    {/* Live Staging Progress */}
                    <TableCell>
                      {(() => {
                        const matId = plan.plannedMaterial?.id || plan.plannedMaterialId || plan.materialId;
                        const matCode = plan.plannedMaterial?.code || plan.plannedMaterial?.materialId || plan.materialCode;
                        // Chỉ tính lần cấp theo kế hoạch đã xuất thật; hàng mới mua về cho lệnh hiện "Đã về kho, chờ cấp" (FB-CDA00F)
                        const qtyIss = stagedLogs
                          .filter((l: any) => (matId && l.materialId === matId) || (matCode && l.materialCode === matCode))
                          .filter(isRealIssueLog)
                          .reduce((s: number, l: any) => s + Number(l.quantity || 0), 0);
                        const qtyReq = Number(plan.qtyRequired || plan.plannedQuantity || 1);
                        const percent = Math.min(100, Math.round((qtyIss / qtyReq) * 100));
                        const isFulfilled = qtyIss >= qtyReq;
                        const isReceivedWaiting = !isFulfilled && qtyIss + Number(plan.qtyReceivedForPlan || 0) >= qtyReq;

                        return (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between text-[11px] font-mono">
                              <span className="font-bold text-slate-700">
                                {qtyIss.toLocaleString('vi-VN')} / {qtyReq.toLocaleString('vi-VN')} {plan.unit}
                              </span>
                              <span className={`font-bold ${isFulfilled ? 'text-emerald-600' : qtyIss > 0 ? 'text-blue-600' : 'text-slate-400'}`}>
                                {percent}%
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  isFulfilled ? 'bg-emerald-500' : qtyIss > 0 ? 'bg-blue-500' : 'bg-slate-300'
                                }`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <div className="text-center">
                              {isFulfilled ? (
                                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[9px] py-0 h-4">
                                  ✓ ĐÃ CẤP ĐỦ
                                </Badge>
                              ) : isReceivedWaiting ? (
                                <Badge className="bg-sky-100 text-sky-800 border-sky-300 text-[9px] py-0 h-4">
                                  ĐÃ VỀ KHO, CHỜ CẤP
                                </Badge>
                              ) : qtyIss > 0 ? (
                                <Badge className="bg-amber-100 text-amber-900 border-amber-300 text-[9px] py-0 h-4">
                                  ĐANG CẤP {percent}%
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-slate-500 text-[9px] py-0 h-4">
                                  CHƯA CẤP
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </TableCell>

                    <TableCell className="text-right sticky right-0 z-10 bg-white shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.15)]">
                      {editingPlan === plan.id ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingPlan(null)}
                          >
                            Hủy
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 bg-slate-800 text-white"
                            onClick={() => saveEdit(plan.id)}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex justify-end items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 font-semibold px-2 text-xs"
                            onClick={() => setStagingPlan(plan)}
                            disabled={remainingQtyOf(plan) <= 0}
                            data-testid="po-material-row-issue"
                            title="Cấp vật tư từ kho ra sàn máy"
                          >
                            <Package className="h-3.5 w-3.5 mr-1 text-amber-700" />
                            Cấp vật tư
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-500 hover:text-indigo-600"
                            title="Sửa bù hao"
                            aria-label="Sửa bù hao"
                            onClick={() => {
                              setEditingPlan(plan.id);
                              setEditForm({
                                wastePercentage: plan.wastePercentage,
                                setupSheets: 0,
                              });
                            }}
                            disabled={isPlanLocked}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
              })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="text-center py-6 text-slate-500"
                  >
                    Không có vật tư phụ.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Lịch Sử Cấp Phát Vật Tư & Lô Pallet Ra Xưởng */}
      <Card className="shadow-sm border-slate-200 bg-white" data-testid="po-material-issue-log">
        <CardHeader className="bg-slate-50/50 py-2.5 border-b border-slate-100 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            Lịch sử cấp phát
            <Badge variant="outline" className="font-mono text-[10px]">{issueRows.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow className="text-xs">
                <TableHead className="w-12 text-center">STT</TableHead>
                <TableHead>Thời Gian</TableHead>
                <TableHead>Số Phiếu Xuất</TableHead>
                <TableHead>Vật Tư</TableHead>
                <TableHead className="text-right">Số Lượng</TableHead>
                <TableHead>Công Đoạn Nhận</TableHead>
                <TableHead>Kho Xuất</TableHead>
                <TableHead className="text-center w-32">Trạng Thái</TableHead>
                <TableHead className="text-right w-40">Thao Tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {issueRows.length > 0 ? (
                issueRows.map((log: any, idx: number) => (
                  <TableRow key={log.id} className="text-xs hover:bg-amber-50/30">
                    <TableCell className="text-center font-mono">{idx + 1}</TableCell>
                    <TableCell className="text-slate-600">
                      {log.createdAt ? format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm') : '—'}
                    </TableCell>
                    <TableCell className="font-mono font-bold text-indigo-900">
                      {log.docNo || 'XUAT-SX'}
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-slate-900">[{log.materialCode}]</span> {log.materialName}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold text-red-600">
                      {Number(log.quantity).toLocaleString('vi-VN')} {log.unit}
                    </TableCell>
                    <TableCell className="font-semibold text-slate-700">
                      {log.routingStepName || 'In Offset'}
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {log.warehouseName}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                        SẴN SÀNG SÀN MÁY
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[11px] font-semibold border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 flex items-center gap-1 shadow-xs"
                          onClick={() => handleReprint(log, 'PALLET_DECAL')}
                          title="In Thẻ Pallet Decal có mã QR dán kiện"
                        >
                          <Tag className="h-3 w-3 text-amber-700" />
                          Thẻ pallet
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[11px] font-semibold border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100 flex items-center gap-1 shadow-xs"
                          onClick={() => handleReprint(log, 'DOCUMENT')}
                          title="In Phiếu Xuất Cấp Phát Sàn Xưởng A4"
                        >
                          <Printer className="h-3 w-3 text-indigo-700" />
                          Phiếu A4
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-6 text-slate-500">
                    Chưa cấp vật tư nào. Bấm <strong>[Cấp vật tư]</strong> trên dòng vật tư.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Quản Trị Cấp Bù Phế Phẩm & Lịch Sử Cấp Phát Có Phê Duyệt */}
      <Card className="shadow-sm border-slate-200 bg-white" data-testid="po-material-scrap-log">
        <CardHeader className="bg-slate-50/50 py-2.5 border-b border-slate-100 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            Cấp bù phế phẩm
            <Badge variant="outline" className="font-mono text-[10px]">{scrapLogs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow className="text-xs">
                <TableHead className="w-12 text-center">STT</TableHead>
                <TableHead>Thời Gian</TableHead>
                <TableHead>Phân Loại</TableHead>
                <TableHead>Vật Tư</TableHead>
                <TableHead className="text-right">Số Lượng Cấp</TableHead>
                <TableHead>Công Đoạn Lỗi</TableHead>
                <TableHead>Lý Do Phế Phẩm</TableHead>
                <TableHead className="text-center">Trạng Thái Duyệt</TableHead>
                <TableHead>Người Duyệt / Ghi Chú</TableHead>
                <TableHead className="text-right w-36">Thao Tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {supplyLogs.length > 0 ? (
                supplyLogs.map((log: any, idx: number) => {
                  const isScrap = log.issueType === 'SCRAP_COMPENSATION';
                  const isPending = log.approvalStatus === 'PENDING_APPROVAL';
                  const isApproved = log.approvalStatus === 'APPROVED';
                  const isRejected = log.approvalStatus === 'REJECTED';

                  return (
                    <TableRow key={log.id} className={`text-xs hover:bg-slate-50 ${isScrap ? 'bg-rose-50/20' : ''}`}>
                      <TableCell className="text-center font-mono">{idx + 1}</TableCell>
                      <TableCell className="text-slate-600 font-mono">
                        {log.createdAt ? format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm') : '—'}
                      </TableCell>
                      <TableCell>
                        {isScrap ? (
                          <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px] font-bold">
                            CẤP BÙ PHẾ PHẨM
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-100 text-slate-700 text-[10px]">
                            ĐỊNH MỨC KHO
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="font-bold text-slate-900">[{log.material?.materialId || log.materialId}]</span>{' '}
                        {log.material?.name || log.material?.materialName || 'Vật tư'}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-rose-700">
                        {Number(log.quantity).toLocaleString('vi-VN')} {log.unit || 'đơn vị'}
                      </TableCell>
                      <TableCell className="font-semibold text-slate-700">
                        {log.routingStep?.name || log.defectProcessId || '—'}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-slate-600" title={log.defectReason || log.note}>
                        {log.defectReason || log.note || '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        {isPending && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 animate-pulse text-[10px]">
                            <Clock className="h-3 w-3 mr-1" /> CHỜ DUYỆT
                          </Badge>
                        )}
                        {isApproved && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                            <Check className="h-3 w-3 mr-1" /> ĐÃ DUYỆT
                          </Badge>
                        )}
                        {isRejected && (
                          <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-300 text-[10px]">
                            <X className="h-3 w-3 mr-1" /> TỪ CHỐI
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600 text-[11px]">
                        {log.approver?.fullName || log.approvedBy ? (
                          <div>
                            <span className="font-medium text-slate-800">{log.approver?.fullName || log.approvedBy}</span>
                            {log.note && <div className="text-[10px] text-slate-400 truncate">{log.note}</div>}
                          </div>
                        ) : (
                          <span className="italic text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isPending ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1"
                              onClick={() => {
                                setSelectedCompensationLog(log);
                                setApprovalDecision(true);
                                setApprovalNotes('Đồng ý cấp bù phế phẩm theo đề xuất xưởng');
                                setIsApprovalDialogOpen(true);
                              }}
                            >
                              <Check className="h-3 w-3" /> Duyệt
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50 flex items-center gap-1"
                              onClick={() => {
                                setSelectedCompensationLog(log);
                                setApprovalDecision(false);
                                setApprovalNotes('Từ chối: vượt quá tỷ lệ hao hụt cho phép');
                                setIsApprovalDialogOpen(true);
                              }}
                            >
                              <X className="h-3 w-3" /> Bác
                            </Button>
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs italic">Hoàn tất</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-6 text-slate-500">
                    Chưa có yêu cầu cấp bù. Bấm <strong>[Cấp bù]</strong> ở đầu bảng Vật tư in.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Phase 225: Danh Sách Phiếu Xuất Kho WMS Của Lệnh */}
      <Card className="border-slate-200 shadow-xs" data-testid="po-material-docs">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 py-2.5 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
            Chứng từ kho
            <Badge variant="outline" className="font-mono text-[10px]">{inventoryDocs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead className="w-12 text-center">STT</TableHead>
                <TableHead className="w-36">Mã Phiếu Kho</TableHead>
                <TableHead className="w-28 text-center">Loại Giao Dịch</TableHead>
                <TableHead className="w-36">Kho Xuất / Nhập</TableHead>
                <TableHead className="w-28 text-center">Trạng Thái</TableHead>
                <TableHead className="w-24 text-right">Số Dòng</TableHead>
                <TableHead className="w-32 text-center">Ngày Lập</TableHead>
                <TableHead>Diễn Giải / Ghi Chú</TableHead>
                <TableHead className="w-28 text-right">Thao Tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inventoryDocs.length > 0 ? (
                inventoryDocs.map((doc: any, idx: number) => {
                  const isCompleted = doc.status === 'COMPLETED';
                  const isDraft = doc.status === 'DRAFT';
                  const isPendingApproval = doc.status === 'PENDING_APPROVAL';
                  return (
                    <TableRow key={doc.id}>
                      <TableCell className="text-center font-mono text-xs">{idx + 1}</TableCell>
                      <TableCell>
                        <span className="font-mono font-bold text-indigo-900 text-xs">{doc.docNo}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={doc.docType === 'ISSUE' ? 'bg-amber-50 text-amber-800 border-amber-300 text-[10px]' : 'bg-blue-50 text-blue-800 border-blue-300 text-[10px]'}>
                          {doc.docType === 'ISSUE' ? 'XUẤT KHO' : 'NHẬP KHO'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-700">
                        {doc.sourceWarehouse?.name || doc.targetWarehouse?.name || 'Kho Vật Tư'}
                      </TableCell>
                      <TableCell className="text-center">
                        {isCompleted ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                            ✓ Đã Duyệt
                          </Badge>
                        ) : isDraft ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                            Bản Nháp
                          </Badge>
                        ) : isPendingApproval ? (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px]">
                            Chờ Duyệt
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 text-[10px]">
                            {doc.status}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs font-semibold">
                        {(doc.lines || []).length} dòng
                      </TableCell>
                      <TableCell className="text-center text-xs font-mono text-slate-600">
                        {safeFormatDate(doc.docDate, "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 truncate max-w-xs">
                        {doc.notes || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-indigo-600 hover:text-indigo-800 font-semibold"
                          onClick={() => navigate({ to: `/warehouse/material/transactions/${doc.id}` as any })}
                        >
                          Xem Phiếu →
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-6 text-slate-500">
                    Chưa có chứng từ kho. Bấm <strong>[Xuất kho]</strong> ở đầu bảng Vật tư in.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal Yêu Cầu Cấp Bù Phế Phẩm */}
      <Dialog open={isCompensationOpen} onOpenChange={setIsCompensationOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-rose-900 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" />
              Yêu Cầu Cấp Bù Phế Phẩm / Hư Hỏng Sản Xuất
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Yêu cầu cấp thêm nguyên vật liệu ngoài định mức kế hoạch F0 do sự cố máy hoặc phế phẩm công đoạn.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Chọn Vật tư */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Vật tư cần cấp bù (*):</Label>
              <Select
                value={compensationForm.materialId}
                onValueChange={(val) => setCompensationForm((p) => ({ ...p, materialId: val }))}
              >
                <SelectTrigger className="w-full h-9 text-xs">
                  <SelectValue placeholder="Chọn vật tư trong kế hoạch..." />
                </SelectTrigger>
                <SelectContent>
                  {materialPlans.map((mp: any) => (
                    <SelectItem key={mp.id} value={mp.plannedMaterialId || mp.materialId}>
                      [{mp.plannedMaterial?.code || mp.plannedMaterial?.materialId || mp.materialCode}] {mp.plannedMaterial?.name || mp.materialName} ({mp.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Số lượng cấp bù */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Số lượng yêu cầu cấp bù (*):</Label>
              <NumericInput
                value={compensationForm.quantity || ""}
                onChange={(e) => setCompensationForm((p) => ({ ...p, quantity: Number(e.target.value) }))}
                placeholder="VD: 50, 100..."
                className="h-9 text-xs font-mono font-bold text-rose-700"
              />
            </div>

            {/* Công đoạn gây lỗi */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Công đoạn gây lỗi / phát sinh phế phẩm:</Label>
              <Select
                value={compensationForm.defectProcessId || "NONE"}
                onValueChange={(val) => setCompensationForm((p) => ({ ...p, defectProcessId: val === "NONE" ? "" : val }))}
              >
                <SelectTrigger className="w-full h-9 text-xs">
                  <SelectValue placeholder="Chọn công đoạn..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">— Không chỉ định —</SelectItem>
                  {(poDetail?.routingSteps || []).map((step: any) => (
                    <SelectItem key={step.id} value={step.id}>
                      {step.stepOrder}. {step.name} ({step.stepId})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Lý do phế phẩm */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Lý do phế phẩm / hư hỏng (*):</Label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {[
                  "Rách phôi / kẹt giấy máy in",
                  "Lệch màu mực / lem bẩn",
                  "Bế lệch / đứt gân",
                  "Hỏng keo / bong màng",
                  "Hao hụt căn chỉnh máy vượt mức",
                ].map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() => setCompensationForm((p) => ({ ...p, defectReason: reason }))}
                    className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                      compensationForm.defectReason === reason
                        ? "bg-rose-100 text-rose-800 border-rose-300 font-bold"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <Input
                value={compensationForm.defectReason}
                onChange={(e) => setCompensationForm((p) => ({ ...p, defectReason: e.target.value }))}
                placeholder="Nhập chi tiết nguyên nhân phát sinh phế phẩm..."
                className="h-8 text-xs"
              />
            </div>

            {/* Ghi chú thêm */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Ghi chú giải trình thêm:</Label>
              <Input
                value={compensationForm.note}
                onChange={(e) => setCompensationForm((p) => ({ ...p, note: e.target.value }))}
                placeholder="VD: Yêu cầu giao gấp trong ca sáng..."
                className="h-8 text-xs"
              />
            </div>
          </div>

          <DialogFooter className="pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={() => setIsCompensationOpen(false)}>
              Hủy
            </Button>
            <Button
              onClick={handleRequestCompensation}
              disabled={isSubmittingCompensation}
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white font-semibold"
            >
              {isSubmittingCompensation ? "Đang gửi..." : "Gửi Yêu Cầu Cấp Bù"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Phê Duyệt Cấp Bù (Quản Đốc / Trưởng Ca) */}
      <Dialog open={isApprovalDialogOpen} onOpenChange={setIsApprovalDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              Phê Duyệt Cấp Bù Phế Phẩm (Quản Đốc)
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Xác nhận cấp phát thêm vật tư ngoài định mức cho lệnh sản xuất.
            </DialogDescription>
          </DialogHeader>

          {selectedCompensationLog && (
            <div className="space-y-3 py-2 text-xs">
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-1">
                <div><strong>Vật tư:</strong> [{selectedCompensationLog.material?.materialId || selectedCompensationLog.materialId}] {selectedCompensationLog.material?.name || selectedCompensationLog.material?.materialName}</div>
                <div><strong>Số lượng cấp bù:</strong> <span className="text-rose-700 font-bold font-mono text-sm">{Number(selectedCompensationLog.quantity).toLocaleString('vi-VN')}</span> {selectedCompensationLog.unit || 'đơn vị'}</div>
                <div><strong>Công đoạn lỗi:</strong> {selectedCompensationLog.routingStep?.name || selectedCompensationLog.defectProcessId || 'Chung'}</div>
                <div><strong>Lý do hư hỏng:</strong> {selectedCompensationLog.defectReason || selectedCompensationLog.note}</div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Quyết định phê duyệt:</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={approvalDecision ? "default" : "outline"}
                    className={`flex-1 h-8 text-xs ${approvalDecision ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}`}
                    onClick={() => setApprovalDecision(true)}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Đồng Ý Cấp
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={!approvalDecision ? "default" : "outline"}
                    className={`flex-1 h-8 text-xs ${!approvalDecision ? "bg-rose-600 text-white hover:bg-rose-700" : ""}`}
                    onClick={() => setApprovalDecision(false)}
                  >
                    <X className="h-3.5 w-3.5 mr-1" /> Từ Chối
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Ý kiến / Ghi chú của Quản đốc:</Label>
                <Input
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  placeholder="Ghi chú phê duyệt hoặc lý do từ chối..."
                  className="h-8 text-xs"
                />
              </div>
            </div>
          )}

          <DialogFooter className="pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={() => setIsApprovalDialogOpen(false)}>
              Hủy
            </Button>
            <Button
              onClick={handleApproveCompensation}
              disabled={isSubmittingApproval}
              size="sm"
              className={approvalDecision ? "bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" : "bg-rose-600 hover:bg-rose-700 text-white font-semibold"}
            >
              {isSubmittingApproval ? "Đang xử lý..." : approvalDecision ? "Xác Nhận Cấp Bù" : "Xác Nhận Từ Chối"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {stagingPlan && (
        <ShopfloorMaterialStagingDialog
          open={!!stagingPlan}
          onOpenChange={(open) => !open && setStagingPlan(null)}
          productionOrderId={orderId}
          poCode={poDetail?.orderId || poDetail?.poReference || orderId}
          productName={poDetail?.productName || poDetail?.product?.productName || '—'}
          customerName={poDetail?.customer?.name || poDetail?.customerName || ''}
          materialId={stagingPlan.plannedMaterial?.id || stagingPlan.plannedMaterialId || stagingPlan.materialId}
          materialCode={stagingPlan.plannedMaterial?.code || stagingPlan.plannedMaterial?.materialId || stagingPlan.materialCode || 'VT'}
          materialName={stagingPlan.plannedMaterial?.name || stagingPlan.materialName || 'Vật tư'}
          unit={stagingPlan.unit || 'tờ'}
          qtyRequired={Number(stagingPlan.qtyRequired || stagingPlan.plannedQuantity || 0)}
          qtyIssued={Number(stagingPlan.qtyIssued || 0)}
          materialPlanId={stagingPlan.id}
          routingSteps={(poDetail?.routingSteps || []).map((s: any) => ({ id: s.id, name: s.name, stepOrder: s.stepOrder }))}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['material-planning'] });
            queryClient.invalidateQueries({ queryKey: ['production-order', orderId] });
            refetchStagedLogs();
          }}
        />
      )}

      {splitPlan && (
        <SplitMaterialDialog
          open={!!splitPlan}
          onOpenChange={(open) => !open && setSplitPlan(null)}
          materialPlan={splitPlan}
          orderId={orderId}
        />
      )}

      {/* Add Material Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-600" />
              Thêm Vật Tư / Định Mức Phụ Cho Lệnh Sản Xuất
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Khai báo vật tư phụ liên kết trực tiếp với công đoạn sản xuất để xuất kho và theo dõi WMS.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Chọn công đoạn liên kết */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Công đoạn áp dụng:</Label>
              <Select
                value={addForm.routingStepId || "NONE"}
                onValueChange={(val) => {
                  const stepId = val === "NONE" ? null : val;
                  const selectedStep = (poDetail?.routingSteps || []).find((s: any) => s.id === stepId);
                  const sName = (selectedStep?.name || "").toLowerCase();
                  const isLam = sName.includes("cán") || sName.includes("màng");
                  setIsLamination(isLam);

                  setAddForm((prev: any) => ({
                    ...prev,
                    routingStepId: stepId,
                    specification: selectedStep ? `Vật tư cho công đoạn ${selectedStep.name}` : prev.specification,
                  }));
                }}
              >
                <SelectTrigger className="w-full h-9 text-xs">
                  <SelectValue placeholder="Chọn công đoạn liên kết..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">— Không liên kết công đoạn cụ thể —</SelectItem>
                  {(poDetail?.routingSteps || []).map((step: any) => (
                    <SelectItem key={step.id} value={step.id}>
                      {step.stepOrder}. {step.name} ({step.stepId})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Chọn vật tư từ danh mục */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Vật tư từ kho:</Label>
              <SharedCombobox
                type="material"
                value={addForm.materialId || ""}
                onChange={(val, opt) => {
                  const orig = opt?.original || {};
                  setAddForm({
                    ...addForm,
                    materialId: val,
                    unit: orig.unit || addForm.unit || "Tờ",
                  });
                }}
                placeholder="Chọn hoặc tìm kiếm vật tư..."
                className="h-9 text-xs"
              />
            </div>
            
            {/* Checkbox màng cán tính mét tới */}
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox id="isLamination" checked={isLamination} onCheckedChange={(c) => setIsLamination(!!c)} />
              <Label htmlFor="isLamination" className="font-semibold text-xs text-indigo-700 cursor-pointer">
                Đây là màng cán nhiệt (Tự động tính mét dài & m²)
              </Label>
            </div>
            
            {isLamination && (
              <div className="bg-indigo-50/50 p-3 rounded-lg border border-indigo-100 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Cạnh ngắn khổ in (cm):</Label>
                    <NumericInput
                      value={paperShortEdge}
                      onChange={(e) => setPaperShortEdge(Number(e.target.value))}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Số tờ in chạy máy (Tờ):</Label>
                    <NumericInput
                      value={paperQty}
                      onChange={(e) => setPaperQty(Number(e.target.value))}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="lamination2Sides"
                    checked={lamination2Sides}
                    onCheckedChange={(c) => setLamination2Sides(!!c)}
                  />
                  <Label htmlFor="lamination2Sides" className="text-xs text-slate-700 cursor-pointer">
                    Cán 2 mặt (Nhân đôi chiều dài màng)
                  </Label>
                </div>
                <p className="text-[11px] text-slate-500 italic">
                  • Định mức mét dài = (Khổ in / 100) x Số tờ x {lamination2Sides ? "2 mặt" : "1 mặt"} x 1.05 (bù hao 5%)
                </p>
              </div>
            )}

            {/* Quy cách kỹ thuật */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-700">Quy cách / Diễn giải:</Label>
              <Input
                value={addForm.specification || ""}
                onChange={(e) => setAddForm({ ...addForm, specification: e.target.value })}
                placeholder="VD: Màng BOPP mờ khổ 43cm, Nhũ vàng 64x120m, Keo phá màng..."
                className="h-8 text-xs"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-700">Số lượng (Net):</Label>
                <NumericInput
                  value={addForm.qtyRequired || ""}
                  onChange={(e) => setAddForm({ ...addForm, qtyRequired: Number(e.target.value) })}
                  placeholder="VD: 1000"
                  className="h-8 text-xs font-mono font-bold text-indigo-700"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-700">Đơn vị (UOM):</Label>
                <Input
                  value={addForm.unit || ""}
                  onChange={(e) => setAddForm({ ...addForm, unit: e.target.value })}
                  placeholder="Mét, Cuộn, Kg, Tờ..."
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-700">Bù hao (%):</Label>
                <NumericInput
                  value={addForm.wastagePercent !== undefined ? addForm.wastagePercent : 5}
                  onChange={(e) => setAddForm({ ...addForm, wastagePercent: Number(e.target.value) })}
                  placeholder="VD: 5"
                  className="h-8 text-xs font-mono"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-2 border-t">
            <Button variant="ghost" size="sm" onClick={() => setIsAddOpen(false)}>Hủy</Button>
            <Button onClick={handleAddMaterial} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              Lưu Định Mức
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprint Dialog for Staged Log */}
      {reprintData && (
        <Dialog open={!!reprintData} onOpenChange={(open) => !open && setReprintData(null)}>
          <DialogContent className="w-[95vw] sm:max-w-3xl md:max-w-4xl lg:max-w-4xl xl:max-w-5xl max-h-[92vh] flex flex-col p-0 overflow-hidden">
            <DialogHeader className="p-4 bg-slate-900 text-white flex flex-row items-center justify-between">
              <div>
                <DialogTitle className="text-base font-bold text-white flex items-center gap-2">
                  <Printer className="h-4 w-4 text-amber-400" />
                  In Lại Phiếu Cấp
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-300">
                  Số phiếu: {reprintData.data.docNo} • SL: {reprintData.data.totalQuantity.toLocaleString('vi-VN')} {reprintData.data.unit}
                </DialogDescription>
              </div>

              <div className="flex items-center gap-2">
                <div className="bg-slate-800 p-1 rounded-lg flex gap-1 border border-slate-700">
                  <Button
                    size="sm"
                    variant={reprintData.mode === 'DOCUMENT' ? 'default' : 'ghost'}
                    onClick={() => setReprintData({ ...reprintData, mode: 'DOCUMENT' })}
                    className="h-7 text-xs px-2.5"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" />
                    Phiếu Xuất A4
                  </Button>
                  <Button
                    size="sm"
                    variant={reprintData.mode === 'PALLET_DECAL' ? 'default' : 'ghost'}
                    onClick={() => setReprintData({ ...reprintData, mode: 'PALLET_DECAL' })}
                    className="h-7 text-xs px-2.5"
                  >
                    <Tag className="h-3.5 w-3.5 mr-1" />
                    Tem Pallet Decal
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-6 bg-slate-100 flex justify-center">
              <div className="shadow-lg bg-white">
                <MaterialStagingPrintTemplate ref={reprintRef} data={reprintData.data} mode={reprintData.mode} />
              </div>
            </div>

            <DialogFooter className="p-4 bg-white border-t border-slate-200 flex justify-between items-center sm:justify-between">
              <Button variant="outline" size="sm" onClick={() => setReprintData(null)}>
                Đóng
              </Button>
              <Button
                size="sm"
                onClick={handleTriggerReprint}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold flex items-center gap-1.5"
              >
                <Printer className="h-4 w-4" />
                In Ngay ({reprintData.mode === 'PALLET_DECAL' ? 'Tem Decal' : 'Phiếu A4'})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Phase 225: Modal In Bảng Kê Nhặt Hàng (Picking List) */}
      <MaterialPickingListPrintModal
        isOpen={showPickingModal}
        onClose={() => { setShowPickingModal(false); setPickingFilter(null); }}
        poCode={poDetail?.orderId || poDetail?.poReference || orderId}
        productName={poDetail?.productName || poDetail?.product?.productName || 'Sản phẩm'}
        customerName={poDetail?.customer?.name || poDetail?.customerName || ''}
        createdDate={poDetail?.createdAt || new Date()}
        items={pickingFilter ? pickingItems.filter((it) => pickingFilter.has(it.id)) : pickingItems}
      />
    </div>
  );
}
